/**
 * Рахунки обліку договорів (0372) — матриця правил за Нормою #25.
 *
 * Дзеркало payrollAccountRules: правила (org × договір/контрагент/вид) +
 * effective-резолв для вкладки картки договору (Закон #6: те саме ядро,
 * що у постингах) + «карткове» правило (PUT /card) + нестандартні.
 */
import { api } from './client';

export interface ContractAccountRule {
  id: string;
  organization_id: string;
  contract_id: string | null;
  contractor_id: string | null;
  contract_type: string | null;
  receivable_account_id: string | null;
  payable_account_id: string | null;
  advance_received_account_id: string | null;
  advance_issued_account_id: string | null;
  vat_obligation_account_id: string | null;
  vat_unconfirmed_account_id: string | null;
  // П3 (0376): розрахунки за зворотною тарою (застава).
  tare_receivable_account_id: string | null;
  tare_payable_account_id: string | null;
  priority: number;
  is_active: boolean;
  comment: string | null;
  external_source?: string | null;
}

export type ContractAccountRuleIn = Omit<
  ContractAccountRule, 'id' | 'external_source'
>;

export interface EffectiveAccount {
  purpose: string;
  account_id: string | null;
  code: string | null;
  source: string; // rule:contract | rule:contractor | rule:type | rule:org | non_resident | default
}

export interface CardRuleIn {
  organization_id: string;
  contract_id: string;
  receivable_account_id?: string | null;
  payable_account_id?: string | null;
  advance_received_account_id?: string | null;
  advance_issued_account_id?: string | null;
  vat_obligation_account_id?: string | null;
  vat_unconfirmed_account_id?: string | null;
  tare_receivable_account_id?: string | null;
  tare_payable_account_id?: string | null;
}

export interface NonstandardContract {
  contract_id: string;
  contract_name: string;
  contractor_name: string;
  rule_id: string;
  accounts: EffectiveAccount[];
}

const BASE = '/refs/contract-account-rules';

export const contractAccountRules = {
  list: (params: { organization_id: string; contract_id?: string }) =>
    api.get<ContractAccountRule[]>(BASE, { params }).then((r) => r.data),
  create: (p: Partial<ContractAccountRuleIn> & { organization_id: string }) =>
    api.post<ContractAccountRule>(BASE, p).then((r) => r.data),
  patch: (id: string, p: Partial<ContractAccountRuleIn>) =>
    api.patch<ContractAccountRule>(`${BASE}/${id}`, p).then((r) => r.data),
  remove: (id: string) => api.delete(`${BASE}/${id}`).then(() => undefined),
  effective: (params: { organization_id: string; contract_id: string }) =>
    api.get<EffectiveAccount[]>(`${BASE}/effective`, { params }).then((r) => r.data),
  putCard: (p: CardRuleIn) =>
    api.put<ContractAccountRule | null>(`${BASE}/card`, p).then((r) => r.data),
  nonstandard: (params: { organization_id: string }) =>
    api.get<NonstandardContract[]>(`${BASE}/nonstandard`, { params }).then((r) => r.data),
};

/** Людські назви призначень рахунків (панель картки + сторінка правил). */
export const PURPOSE_LABELS: Record<string, string> = {
  receivable: 'Розрахунки з покупцем',
  payable: 'Розрахунки з постачальником',
  advance_received: 'Аванси одержані',
  advance_issued: 'Аванси видані',
  vat_obligation: 'ПДВ зобовʼязання (6412)',
  vat_unconfirmed: 'ПДВ непідтверджені (6432)',
  tare_receivable: 'Тара: застава покупця',
  tare_payable: 'Тара: застава постачальнику',
};

export const SOURCE_LABELS: Record<string, string> = {
  'rule:contract': 'правило договору',
  'rule:contractor': 'правило контрагента',
  'rule:type': 'правило виду договору',
  'rule:org': 'правило організації',
  'rule:unusable': 'не застосовується',
  non_resident: 'нерезидент (авто)',
  default: 'типовий',
};
